import React, { ReactNode, useMemo, useState } from "react";
import {
  DialogOverlay as ReachDialogOverlay,
  DialogContent as ReachDialogContent,
} from "@reach/dialog";
import { SystemProps, Icon, Box, Text } from "@storyofams/react-ui";
import { motion, AnimatePresence } from "framer-motion";

import Image from "next/image";
import ReactPlayer from "react-player";
import styled, { css } from "styled-components";
import { Slider } from "~components/common/Slider";
import { Cross, PlayButton } from "../../../../components/common/Icons";
import { Button } from "../../../common/Button";

const MotionOverlay = motion(ReachDialogOverlay);
const MotionDialog = motion(ReachDialogContent);

const DialogOverlay = styled(MotionOverlay)<SystemProps>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  z-index: ${(p) => p.theme.zIndices.modal};

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(0, 0, 0, 0.6);
  opacity: 0.75;
  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    padding: ${(p) => p.theme.space[4]}px ${(p) => p.theme.space[2]}px 92px;
  }
`;

const DialogContent = styled(MotionDialog)<{ children?: ReactNode }>`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 100%;
  background: transparent;
  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    max-width: 774px;
  }
`;

const CloseButton = styled(Box)`
  position: absolute;
  right: 16px;
  top: 0px;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 100px;
  transition: color 0.2s;
  z-index: 2;
`;

const ContentWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 0 auto;
  padding: 40px 16px;
  width: max-content;
  height: max-content;
`;

interface ModalProps extends SystemProps {
  images?: string;
  videos?: string;
}

export const MediaPreview = ({ images, videos }: ModalProps) => {
  const content = useMemo(() => {
    return [
      ...videos
        .trim()
        .split(",")
        .filter((item) => item),
      ...images
        .trim()
        .split(",")
        .filter((item) => item),
    ]
      .map((item) =>
        item.includes(".img") ||
        item.includes(".jpeg") ||
        item.includes(".png") ||
        item.includes(".jpg")
          ? { url: item, type: "image" }
          : { url: item, type: "video" }
      )
      .sort((item) => (item.type === "video" ? 1 : 0));
  }, []);
  const [isOpen, setIsOpen] = useState(false);
  const mediaQuantity = content.length;
  return (
    <Box
      px={[0, 2]}
      ml={[2, 0]}
      display={["none", "block"]}
      borderLeft={["1px solid grey200", "none"]}
      onClick={() => setIsOpen(true)}
      cursor={"pointer"}
      pl={2}
      css={css({
        img: {
          borderRadius: "8px",
        },
        video: {
          borderRadius: "8px",
          objectFit: "cover",
        },
      })}
    >
      {content[0]?.type === "image" ? (
        <Image
          src={content[0]?.url}
          width={95}
          height={170}
          objectFit="cover"
        />
      ) : (
        <Box borderRadius={"md"} position={"relative"} width={95} height={170}>
          <ReactPlayer
            url={content[0]?.url}
            playing={false}
            width={"100%"}
            height={"100%"}
          />
          <Box
            width={"47px"}
            top={8}
            left={"27px"}
            pl="15px"
            pt="7px"
            borderRadius={"4px"}
            height={"35px"}
            bg="white"
            position={"absolute"}
            cursor={"pointer"}
          >
            <Icon icon={PlayButton} fontSize={"20px"} />
          </Box>
        </Box>
      )}
      {mediaQuantity > 1 && (
        <Text
          color="grey600"
          cursor={"pointer"}
          onClick={() => setIsOpen(true)}
        >
          + {mediaQuantity - 1} more
        </Text>
      )}
      <AnimatePresence>
        {isOpen && (
          <DialogOverlay
            onDismiss={() => setIsOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <DialogContent
              aria-label={"image-modal"}
              initial={{ y: -10 }}
              animate={{ y: 0 }}
              exit={{ y: -10 }}
            >
              <ContentWrapper
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
              >
                <CloseButton
                  onClick={() => setIsOpen(false)}
                  aria-label="Close modal"
                >
                  <Icon icon={Cross} fontSize={2.5} color="grey900" />
                </CloseButton>

                <Box
                  width={"600px"}
                  bg="white"
                  className="slider-container"
                  height={["300px", "600px"]}
                  maxHeight={["calc(100vh - 80px)", "calc(100vh - 160px)"]}
                  maxWidth={["calc(100vw - 32px)", "740px"]}
                  overflow="hidden"
                >
                  <Slider perPage={1} isReview>
                    {content.map(({ url, type }) => (
                      <Box
                        height={["300px", "600px"]}
                        maxHeight={[
                          "calc(100vh - 80px)",
                          "calc(100vh - 160px)",
                        ]}
                        maxWidth={["calc(100vw - 32px)", "740px"]}
                        key={url}
                        position={"relative"}
                      >
                        {type === "video" ? (
                          <ReactPlayer
                            url={url}
                            height={600}
                            width={"100%"}
                            playing
                            playsinline
                            muted
                            controls
                            config={{
                              youtube: {
                                playerVars: {
                                  controls: true,
                                },
                              },
                            }}
                          />
                        ) : (
                          <Image src={url} objectFit="cover" layout="fill" />
                        )}
                      </Box>
                    ))}
                  </Slider>
                </Box>
              </ContentWrapper>
            </DialogContent>
          </DialogOverlay>
        )}
      </AnimatePresence>
    </Box>
  );
};
